<template>
  <el-dialog
    v-model="chargeVisible"
    :title="isRefundMode ? '登记客户退款' : '登记客户收款'"
    width="80%"
    @close="handleCancel"
  >
    <div style="display: flex; justify-content: flex-end; margin-bottom: 12px;">
      <el-radio-group v-model="isRefundMode" size="default" @change="handleModeChange">
        <el-radio-button :value="false">登记收款</el-radio-button>
        <el-radio-button :value="true">登记退款</el-radio-button>
      </el-radio-group>
    </div>
    <div class="form-title mb-10">
      <span class="label blue">业</span>
      <span class="left">业务信息</span>
    </div>
    <MiniBusinessList :businessList="chargeDetail" ref="miniBusinessList">
      <template v-slot:before>
        <el-table-column prop="no" label="编号" show-overflow-tooltip width="120"/>
        <el-table-column prop="name" label="名称" show-overflow-tooltip width="100"/>
        <el-table-column prop="brand" label="品牌" show-overflow-tooltip/>
        <el-table-column prop="received" label="已收费用" show-overflow-tooltip align="right" min-width="100">
          <template #default="{ row }">
            {{ $currency(row.received) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="receivedAmount"
          :label="isRefundMode ? '退款金额' : '收款金额'"
          show-overflow-tooltip
          align="right"
          min-width="140"
        >
          <template #default="{ row }">
            <el-input-number
              v-model="row.receivedAmount"
              type="number"
              :min="0"
              :max="isRefundMode ? Number(row.received) : undefined"
              @input="handleFeeInput(row)"
              :precision="2"
            />
          </template>
        </el-table-column>
        <el-table-column
          prop="remainAmount"
          :label="isRefundMode ? '退款后待收费用' : '待收费用'"
          show-overflow-tooltip
          align="right"
          width="130"
        >
          <template #default="{ row }">
           {{ $currency(row.remainAmount) }}
          </template>
        </el-table-column>
      </template>
      <template v-slot:after>
        <el-table-column prop="businessAmount" label="数量" width="60" />
        <el-table-column prop="invoiceStatus" label="开票状态" width="100" show-overflow-tooltip/>
        <el-table-column prop="receivedStatus" label="收款状态" width="100" show-overflow-tooltip/>
        <el-table-column prop="salesMan" label="业务代表" width="100" show-overflow-tooltip/>
        <el-table-column prop="customerCompany" label="客户公司" width="100" show-overflow-tooltip/>
        <el-table-column prop="ticketCompany" label="所属公司" width="100" show-overflow-tooltip/>
        <el-table-column prop="type" label="维修类型" width="100" show-overflow-tooltip/>
        <el-table-column prop="unit" label="单位" width="60" />
        <el-table-column prop="model" label="型号" show-overflow-tooltip/>
        <el-table-column prop="serial" label="序列号" show-overflow-tooltip/>
      </template>
    </MiniBusinessList>

    <div class="form-title mb-10 mt-30">
      <span class="label" :class="isRefundMode ? 'yellow' : 'green'">{{ isRefundMode ? '退' : '费' }}</span>
      <span class="left">{{ isRefundMode ? '退款信息' : '收费信息' }}</span>
    </div>
    <el-form
      ref="dataForm"
      :model="chargeForm"
      :rules="rules"
      label-position="top"
      label-width="100px"
      class="mb-20"
    >
      <div class="underlineForm">
        <el-form-item :label="isRefundMode ? '退款账户' : '公司账户'" prop="accountId">
          <el-select :filterable="true" v-model="chargeForm.accountId" class="filter-item" placeholder="输入关键字搜索" clearable>
            <el-option
              v-for="item in accountOption"
              :key="item.id"
              :label="item.mark"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="isRefundMode ? '退款时间' : '到账时间'" prop="date">
          <el-date-picker
            v-model="chargeForm.date"
            type="date"
            placeholder="请选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            :size="t('table.btnSize')"
          >
          </el-date-picker>
        </el-form-item>
        <el-form-item :label="isRefundMode ? '退款原因/备注' : '收款备注'" prop="paymentMark" class="fullWidth">
          <el-input
            v-model="chargeForm.paymentMark"
            :placeholder="isRefundMode ? '请输入退款原因及备注说明' : '请输入收款备注'"
            type="textarea"
          />
        </el-form-item>
      </div>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleCancel" type="danger">取消</el-button>
        <el-button type="primary" @click="handleCharge">确定</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script lang='ts'>
import { defineComponent, reactive, toRefs, ref, onMounted, unref } from 'vue'
import { ElMessage, ElForm } from 'element-plus'
import { changeFee, refundFee } from '@/apis/received'
import { getPageList } from '@/apis/Form'
import { ArrayRowsItem } from '@/model/Index'
import { Today, numberTimes } from '@/utils/index'
import MiniBusinessList from '@/views/business/businessList/MiniBusinessList.vue'

import { useI18n } from 'vue-i18n'
export default defineComponent({
  name: 'ChargeFee',
  components: {
    MiniBusinessList
  },
  props: {
    api: { type: String, required: false, default: 'business' },
    visible: { type: Boolean, required: true, default: false },
    selectRow: { type: Array, required: true, default: () => [] },
    multiWithCurrency: { type: Function, required: true },
    title: { type: String, required: false, default: '登记收款' },
    isRefund: { type: Boolean, required: false, default: false }
  },

  emits: ['update:visibel', 'submit'],
  setup(props, context) {
    const { t } = useI18n()
    const TableList = ref(null)
    const miniBusinessList = ref(null)
    const dataForm = ref(ElForm)

    const initialRefund = props.isRefund || (props.title && props.title.includes('退款'))

    const dataMap = reactive({
      isRefundMode: initialRefund,
      activeId: 0,
      accountOption: [],
      chargeVisible: props.visible,
      chargeDetail: [],
      multiWithCurrency: props.multiWithCurrency,
      chargeForm: {
        accountId: 0,
        date: Today,
        paymentMark: ''
      },
      rules: {
        accountId: [{
          required: true,
          message: '请填写必填项',
          trigger: 'change'
        }],
        date: [{
          required: true,
          message: '请填写必填项',
          trigger: 'change'
        }]
      },
      receivedNum: {
        total: 0, // 应收
        received: 0, // 实收
        receiving: 0 // 待收
      },
      handleModeChange(val: boolean) {
        dataMap.chargeDetail.forEach((item: any) => {
          const totalPrice = numberTimes(item.orderFee, item.amount, false) as any
          if (val) {
            item.receivedAmount = item.received
            item.remainAmount = (totalPrice - item.received) + Number(item.receivedAmount)
          } else {
            item.receivedAmount = totalPrice - item.received
            item.remainAmount = 0
          }
        })
      },
      handleCharge() {
        const form = unref(dataForm)
        form.validate(async(valid: any) => {
          if (valid) {
            try {
              const businessList = (miniBusinessList.value as any).businessList
              if (dataMap.isRefundMode) {
                for (const kk of businessList) {
                  if (!kk.receivedAmount || kk.receivedAmount <= 0) {
                    ElMessage.error(`业务【${kk.no}】退款金额必须大于0`)
                    return
                  }
                  if (Number(kk.receivedAmount) > Number(kk.received)) {
                    ElMessage.error(`业务【${kk.no}】退款金额(${kk.receivedAmount}元)不得大于已收费用(${kk.received}元)`)
                    return
                  }
                }
              }
              const params = {
                ...dataMap.chargeForm,
                isRefund: dataMap.isRefundMode,
                payments: businessList.map((kk: any) => {
                  return {
                    id: kk.id,
                    amount: kk.receivedAmount
                  }
                })
              }
              const apiCall = dataMap.isRefundMode ? refundFee(params) : changeFee(params)
              apiCall.then(() => {
                ElMessage({
                  message: dataMap.isRefundMode ? '退款登记成功' : '收款登记成功',
                  type: 'success',
                  duration: 1000
                })
                context.emit('update:visibel')
                context.emit('submit')
              })
            } catch (err: any) {
              console.log(err)
            }
          }
        })
      },
      handleFeeInput(item: any) {
        const totalPrice = numberTimes(item.orderFee, item.amount, false) as any
        if (dataMap.isRefundMode) {
          if (Number(item.receivedAmount) > Number(item.received)) {
            ElMessage.warning(`退款金额不得大于已收费用 ${item.received} 元`)
            item.receivedAmount = item.received
          }
          item.remainAmount = (totalPrice - item.received) + Number(item.receivedAmount)
        } else {
          item.remainAmount = totalPrice - item.received - Number(item.receivedAmount)
        }
      },
      handleCancel() {
        context.emit('update:visibel')
      },
      getBusinessIds() {
        return props.selectRow.map((item: any) => item.id)
      }
    })
    onMounted(async() => {
      const result = await getPageList('received/receiving', { businessIds: dataMap.getBusinessIds().join() })
      dataMap.chargeDetail = result
        ? result.map((item: any) => {
          const totalPrice = numberTimes(item.business.orderFee, item.business.amount, false) as any
          const isRef = dataMap.isRefundMode
          return {
            ...item.business,
            received: item.received,
            receivedAmount: isRef ? item.received : (totalPrice - item.received),
            remainAmount: isRef ? (totalPrice - item.received + item.received) : 0
          }
        })
        : []
      getPageList('paymentAccount/dropdown').then((res: any) => {
        dataMap.accountOption = res || []
        dataMap.chargeVisible = true
        dataMap.chargeForm.accountId = res[0].id
      })
    })

    return { TableList, t, ...toRefs(dataMap), miniBusinessList, dataForm }
  }
})
</script>
<style lang="scss">
.charge-detail{
  margin-bottom: 20px;
  display: flex;
  div{
    margin-right: 20px;
    span{
      color: black;
    }
    .label{
      padding: 0;
      line-height: 36px;
      font-size: 14px;
      font-weight: bold;
    }
  }
}
</style>
<style lang="scss">
.charge-detail{
  margin-bottom: 20px;
  display: flex;
  div{
    margin-right: 20px;
    span{
      color: black;
    }
    .label{
      padding: 0;
      line-height: 36px;
      font-size: 14px;
      font-weight: bold;
    }
  }
}
</style>
